import { useMemo } from 'react'
import { useNodes, useReactFlow } from '@xyflow/react'
import { produce } from 'immer'
import { useFlowStore } from '@/stores/flow-store'
import { HeaderWithIcon } from '@/features/flow/_builder/components/ui/header-with-icon'
import { BuilderNode } from '../../../types'
import SidebarPanelHeading from '../../components/sidebar-panel-heading'
import SidebarPanelWrapper from '../../components/sidebar-panel-wrapper'
import { NodeListItem } from './components/node-list-item'
import { NodePropertyPanel } from './components/node-propery-panel'
import { useNodeList } from './hooks/use-node-list'
import IntroductionPropertyPanel from './property-panels/introduction-property-panel'

export function NodePropertiesPanel() {
  const selectedNode = useFlowStore(
    (s) => s.sidebar.panels.nodeProperties.selectedNode
  )

  const setSelectedNode = useFlowStore(
    (s) => s.actions.sidebar.panels.nodeProperties.setSelectedNode
  )

  const nodes = useNodes()

  const nodeList = useNodeList(nodes)

  const { setNodes } = useReactFlow()

  const onNodeClick = (id: string) => {
    setNodes((nds) =>
      produce(nds, (draft) => {
        draft.forEach((node) => {
          node.selected = node.id === id
        })
      })
    )

    setSelectedNode({
      id,
      type: nodeList.find((n) => n.id === id)?.type as BuilderNode,
    })
  }

  const selectedNodeData = useMemo(() => {
    return nodes.find((n) => n.id === selectedNode?.id)?.data
  }, [nodes, selectedNode?.id])

  return (
    <SidebarPanelWrapper>
      <div className='flex flex-col h-40 w-80'>
        <SidebarPanelHeading className='shrink-0'>
          <HeaderWithIcon icon='layers-3' title='Nodes in Flow' />
        </SidebarPanelHeading>

        <div className='flex w-full flex-col gap-1 overflow-y-auto p-1.5'>
          {nodeList.map((node) => (
            <NodeListItem
              key={node.id}
              id={
                node.type === BuilderNode.START || node.type === BuilderNode.END
                  ? undefined
                  : node.id
              }
              title={node.detail.title}
              icon={node.detail.icon}
              selected={selectedNode?.id === node.id}
              pseudoSelected={node.selected}
              onClick={() => {
                onNodeClick(node.id)
              }}
            />
          ))}
        </div>
      </div>

      <div className='flex max-h-[360px] w-80 flex-col 2xl:max-h-[594px]'>
        <SidebarPanelHeading className='shrink-0'>
          <HeaderWithIcon icon='cog' title='Node Properties' />
        </SidebarPanelHeading>

        <div className='flex flex-col w-full overflow-y-auto'>
          {selectedNode ? (
            <NodePropertyPanel
              id={selectedNode.id}
              type={selectedNode.type}
              data={selectedNodeData}
            />
          ) : (
            <IntroductionPropertyPanel />
          )}
        </div>
      </div>
    </SidebarPanelWrapper>
  )
}
